import { isArray } from 'lodash';
import { useDragLayer, useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { FunctionComponent, useEffect, useState } from 'react';
import { getDescendantWidgetIds } from '../../components/Placeholder';
import { folderClosedSvg, folderOpenedSvg } from '../../assets/images';
import { actionCreators } from '../../reducers/templateEditor';
import { StateShape as ReduxStateShape } from '../../reducers';
import { Layer } from './index';

interface LayerItemProps {
    layer: Layer;
    onClickItem?: (id: string) => void;
    onClickAccordion?: () => void;
    isActive?: boolean;
    isClosed?: boolean;
    hasChildren?: boolean;
}

const getParents = (data: Layer[], id: string) => {
    const parents: string[] = [];
    const getNodes = (result: Layer[], node: Layer) => {
        if (node.id === id) {
            result.push(node);
            parents.push(node.id);
            return result;
        }

        if (Array.isArray(node.children)) {
            const children = node.children.reduce(getNodes, []);

            if (children.length) {
                result.push({ ...node, children });
                parents.push(node.id);
            }
        } else if (node.children && Object.keys(node.children).length > 0) {
            Object.keys(node.children).forEach((ch) => {
                const children = node.children[ch].reduce(getNodes, []);

                if (children.length) {
                    result.push({ ...node, children: { [ch]: [...children] } });
                    parents.push(node.id);
                }
            });
        }

        return result;
    };

    data.reduce(getNodes, []);
    return parents;
};

interface NewLayerInputProps {
    parentWidgetInstanceId: string;
    index: number;
    contentPlaceHolderId: string;
}

const NewLayerInput: FunctionComponent<React.PropsWithChildren<NewLayerInputProps>> = ({
    parentWidgetInstanceId,
    contentPlaceHolderId,
    index,
}) => {
    const dispatch = useDispatch();

    const handleDrop = (item: {
        id: string;
        widgetUUID: string;
        dragMode: 'move' | 'copy' | 'create';
    }) => {
        if (item.dragMode === 'move') {
            dispatch(
                actionCreators.moveComponent({
                    parentId: parentWidgetInstanceId,
                    contentPlaceHolderId,
                    widgetUUID: item.widgetUUID,
                    index,
                })
            );
        }
    };

    const [{ isOver }, drop] = useDrop(() => ({
        accept: 'WIDGET_LAYER',
        drop: handleDrop,
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }));

    return (
        <div
            ref={drop}
            className="add-widget-container"
            style={{ backgroundColor: isOver ? '#ededed' : 'white' }}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            {isOver && <img src="add.svg" className="add-widget-icon" alt="add widget" />}
        </div>
    );
};

const LayerItem = (props: LayerItemProps) => {
    const { layer, isActive, isClosed, hasChildren, onClickItem, onClickAccordion } = props;

    return (
        <li
            className={`layer-item ${isActive ? 'layer-active' : ''} ${
                !layer.id ? 'layer-non-clickable' : ''
            }`}
            id={`layer-${layer.id}`}
        >
            {hasChildren && (
                <img
                    id="accordion-icon"
                    className={`accordion-icon`}
                    src={isClosed ? folderClosedSvg : folderOpenedSvg}
                    alt="accordion-icon"
                    onClick={onClickAccordion}
                />
            )}
            <div className={!hasChildren ? 'layer-no-children' : ''}>
                <span onClick={() => layer.id && onClickItem && onClickItem(layer.id)}>
                    {layer.widgetAltName || layer.widgetId}
                </span>
            </div>
        </li>
    );
};

interface LayersProps {
    layers: Layer[];
    initialLayers: Layer[];
    closed?: boolean;
    contentPlaceHolderId?: string;
}

const Layers = (props: LayersProps) => {
    const { initialLayers, layers, closed, contentPlaceHolderId } = props;
    const dispatch = useDispatch();
    const [closedNodes, setClosedNodes] = useState<string[]>([]);

    const { selectedId, widgetInstances } = useSelector((state: ReduxStateShape) => {
        return {
            selectedId: state.templateEditor.selectedId,
            widgetInstances: state.templateEditor.document?.widgetInstances,
        };
    });

    const onClickListItem = (id: string) => {
        dispatch(actionCreators.selectElement(id));
        const el = document.getElementById(`comp-${id}`);
        el?.scrollIntoView({ behavior: 'smooth' });
    };

    const onClickAccordion = (id: string) => {
        if (closedNodes?.includes(id)) {
            setClosedNodes([...closedNodes?.filter((n) => n !== id)]);
        } else {
            setClosedNodes([...closedNodes, id]);
        }
    };

    useEffect(() => {
        if (selectedId) {
            const parentsIds = getParents(initialLayers, selectedId);
            setClosedNodes(closedNodes.filter((node) => !parentsIds.includes(node)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    const {
        item,
        isDragging: isDraggingWidget,
        itemType,
    } = useDragLayer((monitor) => ({
        isDragging: monitor.isDragging(),
        itemType: monitor.getItemType(),
        item: monitor.getItem(),
    }));

    const draggingWidgetId = item?.widgetUUID || '';

    const descendantWidgetIds = draggingWidgetId
        ? getDescendantWidgetIds(widgetInstances || {}, draggingWidgetId)
        : [];

    const showNewLayerInput = isDraggingWidget && itemType === 'WIDGET_LAYER';

    return (
        <div className={`layers ${closed ? 'closed' : ''}`}>
            {layers?.length > 0 &&
                layers?.map((layer: Layer, index: number) => (
                    <ul
                        key={`${layer.id}-${index}`}
                        // eslint-disable-next-line max-len
                        className={`layer ${closedNodes.includes(layer.id) || !layer.children ? '' : 'layer-tree'}`}
                    >
                        <LayerItem
                            layer={layer}
                            onClickItem={onClickListItem}
                            isActive={layer.id === selectedId}
                            hasChildren={!!layer.children}
                            isClosed={closedNodes.includes(layer.id)}
                            onClickAccordion={() => onClickAccordion(layer.id)}
                        />
                        {showNewLayerInput &&
                            layer.id !== 'root' &&
                            !descendantWidgetIds.includes(layer.id) &&
                            !descendantWidgetIds.includes(layer.parentId || '') && (
                                <NewLayerInput
                                    index={index + 1}
                                    parentWidgetInstanceId={layer.parentId || ''}
                                    contentPlaceHolderId={contentPlaceHolderId || 'children'}
                                />
                            )}
                        {layer &&
                            layer.children &&
                            (isArray(layer.children) ? (
                                <Layers
                                    initialLayers={initialLayers}
                                    layers={layer.children}
                                    closed={closedNodes.includes(layer.id)}
                                />
                            ) : (
                                <>
                                    {Object.keys(layer?.children).map((child, index: number) => {
                                        const childLayer = layer.children[child];

                                        return (
                                            <ul
                                                key={`sub-${layer.id}-${index}`}
                                                className={`sub-layer ${
                                                    closedNodes.includes(layer.id) ? 'closed' : ''
                                                }`}
                                            >
                                                <LayerItem
                                                    layer={{
                                                        id: child,
                                                        widgetId: child || 'children',
                                                    }}
                                                />
                                                {showNewLayerInput &&
                                                    !descendantWidgetIds.includes(
                                                        childLayer[0]?.id || ''
                                                    ) &&
                                                    !descendantWidgetIds.includes(child) && (
                                                        <NewLayerInput
                                                            index={0}
                                                            parentWidgetInstanceId={
                                                                childLayer[0]?.parentId || ''
                                                            }
                                                            contentPlaceHolderId={
                                                                child || 'children'
                                                            }
                                                        />
                                                    )}
                                                <Layers
                                                    initialLayers={initialLayers}
                                                    layers={layer.children[child]}
                                                    contentPlaceHolderId={child || 'children'}
                                                />
                                            </ul>
                                        );
                                    })}
                                </>
                            ))}
                    </ul>
                ))}
        </div>
    );
};

export default Layers;
